import { engineComponent, ArrayUtils, StringUtils } from "neo-common";
import { BaseElement } from "./BaseElement";
import { ContainerElement } from "./ContainerElement";
import { SwitchStyle, SwitchStyleCaseAttributes } from "../Styles/SwitchStyle";
import { StyleManager } from "../../Managers/StyleManager";
import { AnimationElement } from "./AnimationElement";
import { FrameTimerEventEmitter, CreateFrameTimer, FrameTimerEvents } from "../../../Utils/FrameTimer";
import { ComponentTypes } from "../../ComponentTypes";
import { ParticleElement } from "./ParticleElement";

/**
 * SwitchElement class is class which creates holder for other elements and holds cases which can change children by switching cases.
 * Can be defined only with SwitchStyle (See SwitchStyle class).
 * Default case is NONE.
 * Example 1:
 *   <SwitchElement Class="SwitchElement" Style="SwitchStyle" />
 *
 * @class SwitchElement
 * @extends BaseElement
 */
@engineComponent(ComponentTypes.DisplayObject)
export class SwitchElement extends BaseElement {
    // event for dispatch end of movie clip
    static AnimationComplete: string = "animationComplete";
    static FinishAnimation: string = "finishAnimation";
    static SwitchedCase: string = "switchedCase";

    // style is mandatory in this element
    protected style: SwitchStyle;
    protected case: string;

    protected elements: ContainerElement;

    protected timer: FrameTimerEventEmitter;

    private _elementsPersistent: boolean = false;

    // TODO clean
    center: boolean;
    // symbol width without offsetX
    private _symbolWidth: number;
    // symbol height without offsetY
    private _symbolHeight: number;

    private dynamicTime = 0;

    constructor() {
        super();

        // default case
        this.case = "NONE";
        this.center = false;

        this.timer = CreateFrameTimer("timer");
    }

    public get symbolWidth(): number {
        return this._symbolWidth;
    }

    public get symbolHeight(): number {
        return this._symbolHeight;
    }

    public set symbolWidth(value: number) {
        this._symbolWidth = value;
    }

    public set symbolHeight(value: number) {
        this._symbolHeight = value;
    }

    public get hasElements(): boolean {
        return this.elements != null && this.elements.childs != null;
    }

    public getChildElementByName(name: string): Object {
        if (this.hasElements) {
            return this.elements.childs[name];
        }

        return null;
    }

    public getChildElements(): Object {
        return this.elements.childs;
    }

    /**
     * Get attributes from xml and set up supported properties.
     * @method create
     * @param node is Element from xml
     * @return void
     */
    createByNode(node: Element) {
        super.createByNode(node);

        if (node.hasAttribute(SwitchElementAttributes.STYLE)) {
            this.style = <SwitchStyle>StyleManager.Styles[node.getAttribute(SwitchElementAttributes.STYLE)];
            if (this.style != null) {
                // get all elements and add them to this display list
                if (this.style.container != null) {
                    if (
                        node.hasAttribute(SwitchElementAttributes.CLONE) &&
                        StringUtils.parseBoolean(node.getAttribute(SwitchElementAttributes.CLONE))
                    ) {
                        this.elements = this.style.container.clone();
                    } else {
                        this.elements = this.style.container;
                    }
                    this.addChild(this.elements);
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no style with name: " + node.getAttribute(SwitchElementAttributes.STYLE));
                }
            }
        }

        if (node.hasAttribute(SwitchElementAttributes.ELEMENTS_PERSISTENT)) {
            this._elementsPersistent = node.getAttribute(SwitchElementAttributes.ELEMENTS_PERSISTENT) == "true";
        }

        if (node.hasAttribute(SwitchElementAttributes.CASE)) {
            let delay: number = null;
            if (node.hasAttribute(SwitchElementAttributes.DELAY)) {
                delay = parseInt(node.getAttribute(SwitchElementAttributes.DELAY));
            }
            this.goToCase(node.getAttribute(SwitchElementAttributes.CASE), false, delay);
        }
    }

    updateByNode(node: Element) {
        super.updateByNode(node);

        if (node.hasAttribute(SwitchElementAttributes.ELEMENTS_PERSISTENT)) {
            this._elementsPersistent = node.getAttribute(SwitchElementAttributes.ELEMENTS_PERSISTENT) == "true";
        }

        if (node.hasAttribute(SwitchElementAttributes.CASE)) {
            let delay: number = null;
            if (node.hasAttribute(SwitchElementAttributes.DELAY)) {
                delay = parseInt(node.getAttribute(SwitchElementAttributes.DELAY));
            }
            this.goToCase(node.getAttribute(SwitchElementAttributes.CASE), false, delay);
        }

        if (node.hasAttribute(SwitchElementAttributes.TINT)) {
            if (this.elements) {
                // first reset Tint to "ffffff" because ContainerElement has return for "this._tint === tintColor"
                this.elements.tint = 0xffffff;
                this.elements.tint = parseInt(node.getAttribute(SwitchElementAttributes.TINT), 16);
            }
        }
    }

    /**
     * Find out if this element has case s.
     * @method hasCase
     * @param s name of case
     * @return Boolean
     */
    hasCase(s: string): Boolean {
        return this.style != null && this.style.cases != null && this.style.cases[s];
    }

    /**
     * Returns xml element of case s.
     * @method getCase
     * @param s name of case
     * @return Element
     */
    getCase(s: string): Element {
        if (this.style != null && this.style.cases != null && this.style.cases[s]) {
            return this.style.cases[s];
        }
        return null;
    }

    public getCurrentCase(): string {
        return this.case;
    }

    /**
     * Switch this elemenet to case s.
     * @method goToCase
     * @param s name of case
     * @param emitEvent boolean
     * @return void
     */
    goToCase(s: string, emitEvent: boolean = false, delay?: number): void {
        if (emitEvent) {
            this.animationComplete();
        }

        let casesArray: Array<string> = [];

        if (casesArray != null && s != null) {
            casesArray = s.split(";");

            if (casesArray.length > 1) {
                let index: number = Math.round(SwitchElement.getRandom(0, casesArray.length - 1));
                s = casesArray[index];
            }
        }

        if (this.style != null && this.style.cases != null && this.case !== s) {
            if (this.hasCase(s)) {
                if (this.timer != null) {
                    this.timer.removeAllListeners(FrameTimerEvents.Complete);
                    this.timer.stop();
                }

                let node: Element = this.style.cases[s];
                if (node != null) {
                    if (DEBUG) {
                        if (node.hasAttribute("Debug") && node.getAttribute("Debug") === "true") {
                            debugger;
                        }
                    }

                    if (node.hasAttribute("FinishHandler")) {
                        this.emit(SwitchElement.FinishAnimation);
                    }

                    if (delay > 0) {
                        this.timer.on(FrameTimerEvents.Complete, this.goToCase.bind(this, s, emitEvent));
                        this.timer.start(delay);
                        return;
                    }

                    this.elements.updateByNode(node);

                    if (this.case != s) {
                        this.emit(SwitchElement.SwitchedCase, s);
                    }

                    let element: any;
                    this.case = s;
                    let next: Element = node.firstElementChild;
                    let arr: Array<string> = [];
                    while (next != null) {
                        arr.push(next.nodeName);
                        element = this.elements.childs[next.nodeName];
                        if (element != null) {
                            element.visible = true;
                            // send new node to element and set / change new attributes

                            /*if(this.center) {
                                if (this._symbolWidth != null) {
                                    element.pivot.x = this._symbolWidth / 2;
                                }
                                if (this._symbolHeight != null) {
                                    element.pivot.y = this._symbolHeight / 2;
                                }
                            }*/
                            element.updateByNode(next);
                        } else {
                            if (DEBUG) {
                                this.log(
                                    "WARNING no element child with name: " +
                                        next.nodeName +
                                        " in case: " +
                                        s +
                                        " in style: " +
                                        this.style.name,
                                );
                            }
                        }
                        next = next.nextElementSibling;
                    }

                    for (let key in this.elements.childs) {
                        this.elements.childs[key].removeAllListeners();

                        let found: boolean = false;
                        for (let i: number = 0; i < arr.length; i++) {
                            if (key === arr[i]) {
                                found = true;
                            }
                        }
                        if (!found) {
                            if (this.elements.childs[key] instanceof ParticleElement) {
                                (this.elements.childs[key] as any as ParticleElement).stop();
                            } else if (this.elements.childs[key] instanceof PIXI.DisplayObject) {
                                if (!this._elementsPersistent) {
                                    this.elements.childs[key].visible = false;
                                }
                            }
                        }
                    }
                    if (node.hasAttribute(SwitchStyleCaseAttributes.NEXT_CASE)) {
                        let casesArray: Array<string> = [];
                        let nextCase: string = node.getAttribute(SwitchStyleCaseAttributes.NEXT_CASE);

                        if (casesArray != null && nextCase != null) {
                            casesArray = nextCase.split(";");

                            if (casesArray.length > 1) {
                                let index: number = Math.round(SwitchElement.getRandom(0, casesArray.length - 1));
                                nextCase = casesArray[index];
                            }
                        }

                        if (nextCase != null && this.hasCase(nextCase)) {
                            if (node.hasAttribute(SwitchStyleCaseAttributes.TIMER)) {
                                let random: number = this.getRandomDurationMultiplier(node);

                                this.timer.on(FrameTimerEvents.Complete, this.goToCase.bind(this, nextCase));
                                if (node.getAttribute(SwitchStyleCaseAttributes.TIMER) === "dynamic") {
                                    this.timer.start(this.dynamicTime * random);
                                } else {
                                    this.timer.start(
                                        parseInt(node.getAttribute(SwitchStyleCaseAttributes.TIMER)) * random,
                                    );
                                }
                            } else if (node.hasAttribute(SwitchStyleCaseAttributes.HANDLER)) {
                                element = this.elements.childs[node.getAttribute(SwitchStyleCaseAttributes.HANDLER)];
                                if (element) {
                                    element.on(
                                        AnimationElement.AnimationComplete,
                                        this.goToCase.bind(this, nextCase, true),
                                    );
                                }
                            } else {
                                this.goToCase(nextCase);
                            }
                        }
                    } else {
                        if (node.hasAttribute(SwitchStyleCaseAttributes.TIMER)) {
                            let random: number = this.getRandomDurationMultiplier(node);

                            //this.timer.on(FrameTimerEvents.Complete, this.goToCase.bind(this, this.animationComplete.bind(this)));
                            this.timer.on(FrameTimerEvents.Complete, this.animationComplete.bind(this));
                            if (node.getAttribute(SwitchStyleCaseAttributes.TIMER) === "dynamic") {
                                this.timer.start(this.dynamicTime * random);
                            } else {
                                this.timer.start(parseInt(node.getAttribute(SwitchStyleCaseAttributes.TIMER)) * random);
                            }
                        } else if (node.hasAttribute(SwitchStyleCaseAttributes.HANDLER)) {
                            element = this.elements.childs[node.getAttribute(SwitchStyleCaseAttributes.HANDLER)];
                            if (element) {
                                element.on(AnimationElement.AnimationComplete, this.animationComplete.bind(this));
                            }
                        }
                    }
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no case: " + s + " for " + this.name);
                }
            }
        }

        if (emitEvent) {
            this.animationComplete();
        }
    }

    private getRandomDurationMultiplier(node: Element): number {
        let result: number = 1;
        if (node.hasAttribute(SwitchElementAttributes.RANDOM)) {
            let dataArray = ArrayUtils.numberArray(node.getAttribute(SwitchElementAttributes.RANDOM));
            if (dataArray.length > 0) {
                let max: number = dataArray[0];
                let min: number = 1;

                if (dataArray.length == 2) {
                    min = dataArray[0];
                    max = min > dataArray[1] ? min : dataArray[1];
                }

                result = Math.round(SwitchElement.getRandom(min, max));
            }
        }

        return result;
    }

    /**
     * Get random case from array of cases.
     * @returns {string}
     * @param s
     */
    private static getRandomCase(s: string): string {
        let result = 0;

        let dataArray: Array<string> = ArrayUtils.stringArray(s);
        if (dataArray.length > 0) {
            let max = dataArray.length - 1;
            let min = 0;

            result = Math.round(SwitchElement.getRandom(min, max));
        } else {
            return null;
        }

        return dataArray[result];
    }

    /**
     * Set time of timer dynamicaly from code.
     * @param {number} time
     * @returns {SwitchElement}
     */
    public setDynamicTime(time: number): SwitchElement {
        this.dynamicTime = time;

        return this;
    }

    /**
     *
     * @param {number} min
     * @param {number} max
     * @returns {number}
     */
    private static getRandom(min: number, max: number): number {
        return Math.random() * (max - min) + min;
    }

    /**
     * Emit complete event when finish changing state.
     * @method animationComplete
     * @return void
     */
    animationComplete(): void {
        this.emit(SwitchElement.AnimationComplete);
    }

    /**
     * Clone method used for cloning instance of this element with his main properties.
     * @method clone
     * @return SwitchElement
     */
    clone(): SwitchElement {
        const element: SwitchElement = new SwitchElement();

        element.name = this.name;
        element.style = this.style;
        element.position.x = this.position.x;
        element.position.y = this.position.y;
        element.pivot.x = this.pivot.x;
        element.pivot.y = this.pivot.y;
        element.scale.x = this.scale.x;
        element.scale.y = this.scale.y;
        element.visible = this.visible;

        element.elements = this.elements.clone();
        element.addChild(element.elements);

        element.case = this.case;

        element.markAsCloned(); // must be called last before return!
        return element;
    }
}

/**
 * XML has attributes SwitchElementAttributes, BaseElementAttributes
 * @type {{STYLE: string; CASE: string}}
 */
export const SwitchElementAttributes = {
    /*
     * Property case is used for set element to some case
     * @property Case
     * @type string
     * @default null
     */
    CASE: "Case",
    CLONE: "Clone",
    DEBUG_INFO: "DebugInfo",
    DEBUG: "Debug",
    DELAY: "Delay",
    /*
     * False: when case changes, all elements not present in the case are hidden
     * True: when case changes, elements not present in the case are left as they were
     * @property ElementsPersistent
     * @type boolean
     * @default false
     */
    ELEMENTS_PERSISTENT: "ElementsPersistent",
    /*
     * Style holds elements as ContainerElement and also holds cases as xml nodes.
     * Style for switch element has to be instance of SwitchStyle.
     * @property Style
     * @type string
     * @default null
     */
    STYLE: "Style",
    /*
     * Multiplies timer with random number between min and max
     * @property Random
     * @type string
     * @default 1
     */
    RANDOM: "Random",
    /*
     * Tint all elements in SwitchElement. Hex color which colorize.
     * @property Tint
     * @type string
     * @default null
     */
    TINT: "Tint",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/Components/Elements/Graphics/SwitchElement.ts
 **/